import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableSortLabel,
  TableHead,
  TableContainer,
  LinearProgress,
  TablePagination
} from '@material-ui/core';
import { AppSnackbar } from '@sierralabs/react-material-ui';

import PageTemplateCardOver from '../../../common/page-template/PageTemplateCardOver';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault
} from 'use-query-params';
import {
  useProductEntityPrefix,
  useFindProductDesignSpecs
} from '../Product.hooks';
import {
  BaseProductEntityTableProps,
  ProductComponent,
  ProductDesignSpec
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';

const DesignSpecificationTable: React.FC<BaseProductEntityTableProps> = (
  props: BaseProductEntityTableProps
) => {
  const { productId, apiParams, fetchRef } = props;
  const history = useHistory();
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);

  const {
    productDesignSpecList,
    isLoadingProductDesignSpecs,
    loadingProductDesignSpecsError,
    findProductDesignSpecs
  } = useFindProductDesignSpecs(productId);

  const findProductDesignSpecsWithParams = useCallback(() => {
    findProductDesignSpecs({ page, limit, order, direction, ...apiParams });
  }, [findProductDesignSpecs, page, limit, order, direction, apiParams]);

  useEffect(() => {
    if (fetchRef) {
      fetchRef.current = findProductDesignSpecsWithParams;
    }
    findProductDesignSpecsWithParams();
  }, [fetchRef, findProductDesignSpecsWithParams]);

  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.DesignSpec
  );

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <PageTemplateCardOver>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignSpec.id'}
                  direction={getSortDirection('productDesignSpec.id')}
                  onClick={() => handleSort('productDesignSpec.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignSpec.title'}
                  direction={getSortDirection('productDesignSpec.title')}
                  onClick={() => handleSort('productDesignSpec.title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignSpec.createdBy'}
                  direction={getSortDirection('productDesignSpec.createdBy')}
                  onClick={() => handleSort('productDesignSpec.createdBy')}
                >
                  Author
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.user'}
                  direction={getSortDirection('task.user')}
                  onClick={() => handleSort('task.user')}
                >
                  Assignee
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'components.name'}
                  direction={getSortDirection('components.name')}
                  onClick={() => handleSort('components.name')}
                >
                  Component
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'release.name'}
                  direction={getSortDirection('release.name')}
                  onClick={() => handleSort('release.name')}
                >
                  Release
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingProductDesignSpecs && (
              <TableRow>
                <TableCell colSpan={3} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {productDesignSpecList?.data?.map(
              (designSpec: ProductDesignSpec) => (
                <TableRow
                  hover
                  key={designSpec.id}
                  onClick={() =>
                    history.push(
                      `/portal/products/${productId}/design-specs/${designSpec.id}`
                    )
                  }
                >
                  <TableCell>{prefix + designSpec.id}</TableCell>
                  <WrappedTableCell>{designSpec.title}</WrappedTableCell>
                  <TableCell>
                    {`${designSpec.task.createdBy?.firstName} ${designSpec.task.createdBy?.lastName}`}
                  </TableCell>
                  <TableCell>
                    {designSpec.task.user
                      ? `${designSpec.task.user?.firstName} ${designSpec.task.user.lastName}`
                      : 'No User Assigned'}
                  </TableCell>
                  <TableCell>
                    <TaskStatusLabel task={designSpec.task} />
                  </TableCell>
                  <TableCell>
                    {designSpec.components.length !== 0
                      ? designSpec.components
                          .map((component: ProductComponent) => component.name)
                          .join(', ')
                      : 'No Components'}
                  </TableCell>
                  <TableCell>
                    {designSpec.release
                      ? designSpec.release.name
                      : 'No Release'}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={productDesignSpecList?.total || 0}
        rowsPerPage={limit}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setLimit(+event.target.value);
          setPage(0);
        }}
      />
      <AppSnackbar
        severity='error'
        open={Boolean(loadingProductDesignSpecsError)}
      >
        There was an error loading the list of{' '}
        {getProductEntityName('designSpec')}.
      </AppSnackbar>
    </PageTemplateCardOver>
  );
};

export default DesignSpecificationTable;
